import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, AlertCircle, Check, Loader2 } from 'lucide-react';
import { VideoPlayer } from '@/components/ui/VideoPlayer';

const TEST_ID = 'internalruralhornedviper';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiResponse = any; // We want to see the full response structure

export const EmbedTestView: React.FC = () => {
  const [apiResult, setApiResult] = useState<{ status: string; data?: ApiResponse; error?: string } | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [thumbnailStatus, setThumbnailStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  // Test proxy API on load
  const tryProxyApi = async () => {
    setLoading(true);
    setApiResult(null);
    setVideoError(null);
    setVideoLoaded(false);

    try {
      const res = await fetch(`/api/video/${TEST_ID}?json&quality=hd`);
      if (!res.ok) throw new Error(`Proxy API failed: ${res.status}`);
      const data = await res.json();
      setApiResult({ status: 'success', data });
    } catch (err) {
      setApiResult({
        status: 'error',
        error: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Try old client-side approach for comparison
  const tryDirectApi = async () => {
    setLoading(true);
    try {
      const tokenRes = await fetch('https://api.redgifs.com/v2/auth/temporary', {
        headers: { 'Accept': 'application/json' },
      });
      if (!tokenRes.ok) throw new Error(`Token failed: ${tokenRes.status}`);
      const tokenData = await tokenRes.json() as { token: string };

      const res = await fetch(`https://api.redgifs.com/v2/gifs/${TEST_ID}`, {
        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${tokenData.token}` },
      });
      if (!res.ok) throw new Error(`API failed: ${res.status}`);
      const data = await res.json();
      setApiResult({ status: 'success', data });
    } catch (err) {
      setApiResult({ status: 'error', error: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-900 p-4 pb-24">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to App
          </Link>
          <h1 className="text-2xl font-bold text-white mb-2">Video Embed Test</h1>
          <p className="text-zinc-400">Testing our proxy-based video player and thumbnail loading.</p>
        </div>

        <div className="space-y-8">

          {/* Test 1: Our VideoPlayer component */}
          <div className="bg-zinc-800 rounded-xl p-6 border border-lime-600">
            <h2 className="text-xl font-bold text-lime-400 mb-2">1. Our VideoPlayer Component</h2>
            <p className="text-zinc-400 text-sm mb-4">Native &lt;video&gt; via <code className="text-xs bg-zinc-900 px-1">/api/video/{TEST_ID}</code> with proxy streaming</p>
            <VideoPlayer url={`https://redgifs.com/watch/${TEST_ID}`} title="Test Clip" />
          </div>

          {/* Test 2: Thumbnail proxy */}
          <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
            <h2 className="text-xl font-bold text-white mb-2">2. Thumbnail Proxy</h2>
            <p className="text-zinc-400 text-sm mb-4">Image via <code className="text-xs bg-zinc-900 px-1">/api/thumbnail/{TEST_ID}</code></p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-zinc-300 text-sm mb-2 font-semibold">Proxy (should work):</p>
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                  <img
                    src={`/api/thumbnail/${TEST_ID}`}
                    alt="Proxy thumbnail"
                    className="w-full h-full object-cover"
                    onLoad={() => setThumbnailStatus('loaded')}
                    onError={() => setThumbnailStatus('error')}
                  />
                </div>
                <p className={`text-sm mt-1 ${thumbnailStatus === 'loaded' ? 'text-green-400' : thumbnailStatus === 'error' ? 'text-red-400' : 'text-zinc-500'}`}>
                  {thumbnailStatus === 'loaded' ? '✓ Loaded' : thumbnailStatus === 'error' ? '✗ Failed' : 'Loading...'}
                </p>
              </div>
              <div>
                <p className="text-zinc-300 text-sm mb-2 font-semibold">Direct CDN (will 403):</p>
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                  <img
                    src={`https://thumbs4.redgifs.com/${TEST_ID}-poster.jpg`}
                    alt="Direct thumbnail"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
                <p className="text-zinc-500 text-sm mt-1">Expected to fail (hotlink blocked)</p>
              </div>
            </div>
          </div>

          {/* Test 3: Proxy API response */}
          <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
            <h2 className="text-xl font-bold text-white mb-2">3. Proxy API Response</h2>
            <p className="text-zinc-400 text-sm mb-4">Test our server-side API endpoints</p>

            <div className="flex gap-2 mb-4">
              <button
                onClick={tryProxyApi}
                disabled={loading}
                className="px-4 py-2 bg-lime-500 text-black font-semibold rounded-lg hover:bg-lime-400 disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Test Proxy API
              </button>
              <button
                onClick={tryDirectApi}
                disabled={loading}
                className="px-4 py-2 bg-zinc-600 text-white font-semibold rounded-lg hover:bg-zinc-500 disabled:opacity-50 flex items-center gap-2"
              >
                Test Direct API
              </button>
            </div>

            {apiResult && (
              <div className={`p-4 rounded-lg ${apiResult.status === 'success' ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'}`}>
                <p className={`font-semibold ${apiResult.status === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                  {apiResult.status === 'success' ? '✓ Success' : '✗ Failed'}
                </p>
                {apiResult.error && <p className="text-red-300 text-sm mt-1">{apiResult.error}</p>}
                {apiResult.data && (
                  <details className="mt-2">
                    <summary className="text-zinc-400 cursor-pointer text-sm">Full Response</summary>
                    <pre className="text-xs bg-zinc-900 p-2 rounded mt-1 overflow-auto max-h-64 text-zinc-300">{JSON.stringify(apiResult.data, null, 2)}</pre>
                  </details>
                )}
              </div>
            )}
          </div>

          {/* Test 4: Native video via proxy stream */}
          <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
            <h2 className="text-xl font-bold text-white mb-2">4. Raw Proxy Stream</h2>
            <p className="text-zinc-400 text-sm mb-4">Direct &lt;video&gt; tag using proxy URL</p>

            <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
              <video
                src={`/api/video/${TEST_ID}`}
                className="w-full h-full"
                controls
                muted
                playsInline
                loop
                poster={`/api/thumbnail/${TEST_ID}`}
                onError={() => setVideoError('Video stream error')}
                onLoadedData={() => setVideoLoaded(true)}
              />
              {videoLoaded && (
                <p className="absolute top-2 right-2 bg-green-500 text-black text-xs px-2 py-1 rounded font-semibold flex items-center gap-1">
                  <Check className="w-3 h-3" /> Streaming
                </p>
              )}
            </div>
            {videoError && (
              <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> {videoError}
              </p>
            )}
          </div>

          {/* Test 5: Old iframe for comparison */}
          <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
            <h2 className="text-xl font-bold text-white mb-2">5. Old Iframe Embed (comparison)</h2>
            <p className="text-zinc-400 text-sm mb-4">Standard RedGifs iframe — shows their branding</p>
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
              <iframe
                src={`https://www.redgifs.com/ifr/${TEST_ID}`}
                className="w-full h-full"
                frameBorder="0"
                scrolling="no"
                allowFullScreen
                allow="fullscreen; autoplay"
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
